import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  preview as previewLockConfirmation,
  send as sendLockConfirmation,
  resend as resendLockConfirmation,
  type CriticalAlert,
  type DocTypeCheck,
  type FailureReason,
  type LastSentInfo,
  type PreviewResponse
} from '@/services/rateLockConfirmationService';
import { cn } from '@/lib/utils';
import { createComponentLogger } from '@/utils/logger';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Loader2, type LucideIcon } from 'lucide-react';

const log = createComponentLogger('ConfirmLockEmailModal', 'rate-lock-confirmation');

/** The server reads the same flag: on, every preview checks the doc type against Optimal Blue. */
const CRITICAL_ALERTS_FLAG = 'rate_lock_critical_alerts_v1';
/** A queued critical email goes out in the background; read the preview again after this. */
const CRITICAL_SEND_REFRESH_MS = 3_000;

interface ConfirmLockEmailModalProps {
  open: boolean;
  encompassLoanId: string;
  onOpenChange: (open: boolean) => void;
  /**
   * Lifts the latest lastSent state into the workspace so the
   * DeliveryLockBanner can render "Sent {timestamp} by {email}" without an
   * extra preview round-trip when the modal is closed. Called both when the
   * preview returns lastSentForCurrentSelection AND after a successful send.
   */
  onLastSentChange?: (info: LastSentInfo | null) => void;
}

const FAILURE_LABELS: Record<FailureReason, string> = {
  notifications_disabled: 'Rate lock confirmation emails are disabled in admin settings.',
  no_active_delivery_lock: 'No active delivery lock is selected for this loan.',
  loan_funded: 'This loan has already funded.',
  brokered_loan: 'Brokered channel loans are not eligible for this notification.',
  no_lo_recipient: 'No active loan officer is assigned.'
};

const PACIFIC_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  dateStyle: 'medium',
  timeStyle: 'short'
});

function formatPacific(iso: string): string {
  try {
    return PACIFIC_TIMESTAMP_FORMATTER.format(new Date(iso));
  } catch {
    /* fall through with raw ISO */
    return iso;
  }
}

function formatLastSentLine(info: LastSentInfo | null, prefix: 'Sent' | 'Already sent'): string | null {
  if (!info) {
return null;
}
  const base = `${prefix} ${formatPacific(info.sentAt)} PT`;
  return info.sentByEmail ? `${base} by ${info.sentByEmail}` : base;
}

type CriticalView =
  | { kind: 'none' }
  | { kind: 'pending'; alert: CriticalAlert }
  | { kind: 'sending' }
  | { kind: 'sent'; alert: CriticalAlert }
  | { kind: 'review_required' };

/**
 * `requested` is set once this modal queued the critical email. Until the
 * server reports it sent, a still-pending alert reads as being sent.
 */
function criticalViewOf(alert: CriticalAlert | null | undefined, requested: boolean): CriticalView {
  if (!alert) {
    return { kind: 'none' };
  }
  switch (alert.status) {
    case 'pending':
      return requested ? { kind: 'sending' } : { kind: 'pending', alert };
    case 'sending':
      return { kind: 'sending' };
    case 'sent':
      return { kind: 'sent', alert };
    case 'review_required':
      return { kind: 'review_required' };
    default:
      return { kind: 'none' };
  }
}

const SENDING_LINE = 'The critical email is being sent.';
const REVIEW_REQUIRED_LINE = "Critical email delivery couldn't be confirmed. An admin needs to review it.";

function mismatchLine(alert: CriticalAlert): string {
  const found = `Doc type mismatch. Encompass: ${alert.encompassValue}. Optimal Blue: ${alert.obValue}. Sending now sends the critical email.`;
  return alert.sendAfter
    ? `${found} If no one sends it, it goes out automatically at ${formatPacific(alert.sendAfter)} PT.`
    : `${found} If no one sends it, it goes out automatically.`;
}

function sentLine(alert: CriticalAlert): string {
  const at = alert.sentAt ? ` at ${formatPacific(alert.sentAt)} PT` : '';
  if (alert.sentVia === 'auto') {
    return `Critical email sent automatically${at}.`;
  }
  if (alert.sentVia === 'manual' && alert.requestedByName) {
    return `Critical email sent at the request of ${alert.requestedByName}${at}.`;
  }
  return `Critical email sent${at}.`;
}

function notCheckedLine(check: DocTypeCheck): string {
  const reason = check.reasonLabel ? ` (${check.reasonLabel})` : '';
  // Promise a background check only when the server will make one.
  const retry = check.retryable ? ' It will be checked again in the background.' : '';
  return `Doc type not checked against Optimal Blue${reason}.${retry}`;
}

type NoticeTone = 'critical' | 'caution' | 'progress' | 'success';

// The pricing-v2 scope's semantic tokens. That scope redefines --border,
// --muted and --accent, so the portal's border/muted/accent utilities do not
// resolve inside this dialog.
const NOTICE_TONE_CLASSES: Record<NoticeTone, string> = {
  critical:
    'border-[color:var(--neg-soft)] border-l-[color:var(--neg)] bg-[color:var(--neg-soft)] text-[color:var(--neg-ink)]',
  caution:
    'border-[color:var(--warn-soft)] border-l-[color:var(--warn)] bg-[color:var(--warn-soft)] text-[color:var(--warn-ink)]',
  progress:
    'border-[color:var(--border)] border-l-[color:var(--border-hard)] bg-[color:var(--surface-2)] text-[color:var(--ink-2)]',
  success:
    'border-[color:var(--pos-soft)] border-l-[color:var(--pos)] bg-[color:var(--pos-soft)] text-[color:var(--pos-ink)]'
};

function DocTypeNotice({
  tone,
  icon: Icon,
  spin = false,
  children
}: {
  tone: NoticeTone;
  icon: LucideIcon;
  spin?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      data-doc-type-notice={tone}
      className={cn(
        'flex items-start gap-2.5 rounded-lg border border-l-4 px-3.5 py-2.5 text-xs leading-snug',
        NOTICE_TONE_CLASSES[tone]
      )}
    >
      <Icon aria-hidden="true" className={cn('mt-px h-4 w-4 shrink-0', spin && 'motion-safe:animate-spin')} />
      <p className="min-w-0 break-words">{children}</p>
    </div>
  );
}

/** Mounted only while the preview loads, so the flag is read when the modal opens. */
function PreviewLoadingText() {
  const criticalChecks = useFeatureFlag(CRITICAL_ALERTS_FLAG);
  return <p>{criticalChecks.enabled ? 'Checking the doc type with Optimal Blue…' : 'Loading preview...'}</p>;
}

export function ConfirmLockEmailModal({ open, encompassLoanId, onOpenChange, onLastSentChange }: ConfirmLockEmailModalProps) {
  const [previewState, setPreviewState] = useState<
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'ready'; data: PreviewResponse }
    | { status: 'error'; message: string }
  >({ status: 'idle' });
  const [sending, setSending] = useState(false);
  // Set once this open has queued the critical email.
  const [criticalRequested, setCriticalRequested] = useState(false);
  // One id per open, reused by repeated critical sends so a replay returns the first result.
  const requestIdRef = useRef<string | null>(null);
  // Bumped by every preview read and by closing, so a stale response never lands.
  const previewSeqRef = useRef(0);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Radix returns focus only to a Dialog.Trigger, and this modal is opened
  // from the delivery-lock banner, so it returns focus to its opener itself.
  const openerRef = useRef<HTMLElement | null>(null);

  const loadPreview = useCallback(async (mode: 'open' | 'refresh') => {
    const seq = ++previewSeqRef.current;
    if (mode === 'open') {
      setPreviewState({ status: 'loading' });
    }
    try {
      const data = await previewLockConfirmation(encompassLoanId);
      if (seq !== previewSeqRef.current) {
        return;
      }
      setPreviewState({ status: 'ready', data });
      // Lift the server-side lastSent into the workspace so the banner can
      // render "Sent {timestamp} by {email}" without an extra round-trip.
      onLastSentChange?.(data.lastSentForCurrentSelection);
    } catch (err: unknown) {
      if (seq !== previewSeqRef.current) {
        return;
      }
      log.error('preview failed', { operation: mode, metadata: { encompassLoanId } }, err instanceof Error ? err : undefined);
      // A failed refresh keeps the current view; the next open reads again.
      if (mode === 'open') {
        setPreviewState({ status: 'error', message: err instanceof Error ? err.message : 'Failed to load preview' });
      }
    }
  }, [encompassLoanId, onLastSentChange]);

  useEffect(() => {
    if (!open) {
      setPreviewState({ status: 'idle' });
      setSending(false);
      setCriticalRequested(false);
      return;
    }
    setCriticalRequested(false);
    requestIdRef.current = crypto.randomUUID();
    void loadPreview('open');
    return () => {
      previewSeqRef.current += 1;
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [open, loadPreview]);

  const scheduleRefresh = () => {
    if (refreshTimerRef.current !== null) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      void loadPreview('refresh');
    }, CRITICAL_SEND_REFRESH_MS);
  };

  const handleSend = async () => {
    const ready = previewState.status === 'ready' ? previewState.data : null;
    const view = criticalViewOf(ready?.criticalAlert, criticalRequested).kind;
    // A busy button stays focusable (aria-disabled), so its clicks land here.
    if (sending || view === 'sending') {
return;
}
    setSending(true);
    try {
      // While the critical email is pending, the send button sends it, even
      // when a normal confirmation already went out.
      const critical = view === 'pending';
      const isResend = !critical && !!ready?.lastSentForCurrentSelection && !!ready?.resendAttemptId;
      // Every send and resend carries this open's request id, so a replayed
      // request returns its original result instead of sending again.
      const options = { acceptsQueued: true, requestId: requestIdRef.current ?? undefined };
      const result = critical
        ? await sendLockConfirmation(encompassLoanId, options)
        : isResend
          ? await resendLockConfirmation(encompassLoanId, ready!.resendAttemptId as string, options)
          : await sendLockConfirmation(encompassLoanId, options);
      if (result.ok) {
        if (result.queued === true) {
          // Handed to the background sender: being sent, never shown as
          // delivered. The refresh shows the sent state once it has gone out.
          const { criticalAlert } = result;
          setCriticalRequested(true);
          setPreviewState((current) => (
            current.status === 'ready' ? { status: 'ready', data: { ...current.data, criticalAlert } } : current
          ));
          scheduleRefresh();
          return;
        }
        toast.success(isResend ? 'Rate lock confirmation re-sent.' : 'Rate lock confirmation sent.');
        // Use the server's canonical sentAt/sentByEmail — never synthesize
        // with new Date() (could drift from the audit trail) and never use
        // selectedByEmail (different person from the actual sender).
        onLastSentChange?.({
          sentAt: result.sentAt,
          sentByEmail: result.sentByEmail,
          resendId: result.resendId
        });
        onOpenChange(false);
        return;
      }
      if (result.code === 'already_sent') {
        const line = formatLastSentLine(result.lastSentForCurrentSelection, 'Already sent');
        toast.info(line ?? 'Already sent for the current selection.');
        if (result.lastSentForCurrentSelection) {
          onLastSentChange?.(result.lastSentForCurrentSelection);
        }
        return;
      }
      if (result.code === 'send_in_flight') {
        toast.info('Another send is currently in flight. Try again in a moment.');
        return;
      }
      if (result.code === 'send_needs_review') {
        toast.error('A prior send attempt needs review before this can be sent again.');
        return;
      }
      if (result.code === 'selection_changed') {
        toast.info('The lock selection changed while the doc type was checked. The preview was refreshed.');
        void loadPreview('refresh');
        return;
      }
      if (result.code === 'doc_type_check_failed') {
        toast.error("The doc type check couldn't finish, so nothing was sent. Try again in a moment.");
        return;
      }
      if (result.code === 'never_sent') {
        // Resend pre-flight saw no prior sent_at row for this selection
        // revision (modal-state desync). Surface and ask the operator to
        // close + reopen the modal — the next preview will return
        // resendAttemptId: null and the modal will land in send mode.
        toast.error('No prior send recorded for this lock. Close the modal and try again.');
        return;
      }
      toast.error(`${isResend ? 'Resend' : 'Send'} failed: ${result.code}${result.detail ? ` — ${result.detail}` : ''}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Send failed';
      log.error('send failed', { metadata: { encompassLoanId } }, err instanceof Error ? err : undefined);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const ready = previewState.status === 'ready' ? previewState.data : null;
  const criticalView = criticalViewOf(ready?.criticalAlert, criticalRequested);
  // A pending or sending critical email replaces the normal send.
  const criticalMode = criticalView.kind === 'pending' || criticalView.kind === 'sending';
  const notChecked = !ready?.criticalAlert && ready?.docTypeCheck?.outcome === 'not_checked'
    ? ready.docTypeCheck
    : null;
  const canSend = criticalMode
    ? criticalView.kind === 'pending' && !sending
    : ready?.gateState.canSend === true && !sending;
  // In flight or being sent: unavailable but still focusable, so keyboard
  // focus stays on the button instead of dropping to the dialog.
  const busy = sending || criticalView.kind === 'sending';
  const reasonLabels = ready
    ? ready.gateState.disabledReasons.map((r) => FAILURE_LABELS[r] ?? r)
    : [];
  const isResendMode = !!ready?.lastSentForCurrentSelection;
  const lastSentLine = formatLastSentLine(ready?.lastSentForCurrentSelection ?? null, 'Sent');
  const dialogTitle = isResendMode ? 'Resend Lock Confirmation to Loan Officer' : 'Confirm Lock to Loan Officer';
  const submitLabel = criticalMode
    ? (sending ? 'Sending...' : 'Send critical email')
    : sending
      ? (isResendMode ? 'Resending...' : 'Sending...')
      : (isResendMode ? 'Resend email with LO pricing' : 'Send email with LO pricing');

  return (
    <Dialog open={open} onOpenChange={(next) => (sending ? null : onOpenChange(next))}>
      <DialogContent
        data-pricing-v2
        className="prm-root w-[calc(100vw-32px)] sm:max-w-[760px] max-h-[calc(100vh-32px)] p-0 gap-0 overflow-hidden"
        onOpenAutoFocus={() => {
          // Runs before focus moves into the dialog.
          openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          openerRef.current?.focus();
          openerRef.current = null;
        }}
      >
        <div className="prm-form">
          <div className="prm-hdr">
            <DialogTitle className="prm-title">{dialogTitle}</DialogTitle>
            <DialogDescription className="prm-desc">
              {isResendMode
                ? 'Re-send the active delivery lock confirmation. The previous email will not be retracted; this delivers a fresh copy to the LO and loan team.'
                : 'Send the active delivery lock as a confirmed rate lock to the LO and loan team.'}
            </DialogDescription>
          </div>

          {/*
            The body scrolls once the preview is long, and the preview iframe is
            its only focusable child, so the region itself takes focus: keyboard
            users can scroll back to the notices (WCAG 2.1.1). The inset ring
            stays visible inside the dialog's overflow clip.
          */}
          <div
            className="prm-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--accent)]"
            role="region"
            aria-label="Confirmation email preview"
            tabIndex={0}
          >
            {/*
              Live regions stay mounted for the whole open so their messages are
              announced (WCAG 4.1.3). Empty, each cancels the body's 14px gap.
            */}
            <div role="alert" aria-atomic="true" className="empty:-mb-[14px]">
              {criticalView.kind === 'pending' && (
                <DocTypeNotice tone="critical" icon={AlertTriangle}>{mismatchLine(criticalView.alert)}</DocTypeNotice>
              )}
              {criticalView.kind === 'review_required' && (
                <DocTypeNotice tone="critical" icon={AlertCircle}>{REVIEW_REQUIRED_LINE}</DocTypeNotice>
              )}
            </div>
            <div role="status" aria-atomic="true" className="empty:-mb-[14px]">
              {previewState.status === 'loading' && <PreviewLoadingText />}
              {criticalView.kind === 'sending' && (
                <DocTypeNotice tone="progress" icon={Loader2} spin>{SENDING_LINE}</DocTypeNotice>
              )}
              {criticalView.kind === 'sent' && (
                <DocTypeNotice tone="success" icon={CheckCircle2}>{sentLine(criticalView.alert)}</DocTypeNotice>
              )}
              {notChecked && (
                <DocTypeNotice tone="caution" icon={Info}>{notCheckedLine(notChecked)}</DocTypeNotice>
              )}
            </div>
            {previewState.status === 'error' && (
              <p style={{ color: '#b91c1c' }}>{previewState.message}</p>
            )}

            {ready && (
              <>
                {ready.sourceMeta && (
                  <div className="prm-finalized">
                    <div className="prm-finalized-hdr">
                      <div>
                        <div className="prm-label">Selected delivery lock</div>
                        <p>
                          {ready.sourceMeta.snapshotLabel ?? 'Lock version'}
                          {ready.sourceMeta.capturedAt
                            ? ` · captured ${formatPacific(ready.sourceMeta.capturedAt)} PT`
                            : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {ready.recipients && (
                  <div className="prm-preview-recipients" style={{ marginTop: 12 }}>
                    <div>
                      <strong>To</strong>
                      <span>{ready.recipients.to.join(', ') || '— no LO resolved'}</span>
                    </div>
                    <div>
                      <strong>CC</strong>
                      <span>{ready.recipients.cc.join(', ') || '—'}</span>
                    </div>
                  </div>
                )}

                {lastSentLine && (
                  <p style={{ marginTop: 12, color: '#4b5563', fontSize: 13 }}>{lastSentLine}</p>
                )}

                {reasonLabels.length > 0 && (
                  <ul style={{ marginTop: 12, color: '#b91c1c', fontSize: 13, paddingLeft: 18 }}>
                    {reasonLabels.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                )}

                {ready.html && (
                  <iframe
                    title="Rate lock confirmation email preview"
                    srcDoc={ready.html}
                    // Empty sandbox = most restrictive: no scripts, no same-origin,
                    // no form submission, no popups. The preview is presentational
                    // HTML rendered in isolation; nothing in it should execute.
                    sandbox=""
                    style={{
                      width: '100%',
                      minHeight: 420,
                      marginTop: 16,
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      background: '#fff'
                    }}
                  />
                )}
              </>
            )}
          </div>

          <div className="prm-footer">
            <button
              type="button"
              className="prm-btn secondary"
              onClick={() => onOpenChange(false)}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="prm-btn primary aria-disabled:!cursor-not-allowed aria-disabled:opacity-[0.55]"
              // Keeps .prm-btn:disabled dimming; the global disabled rule
              // would clear the fill and hide the white label.
              data-preserve-disabled
              onClick={handleSend}
              disabled={!canSend && !busy}
              aria-disabled={busy || undefined}
              title={!canSend && !busy && !criticalMode && reasonLabels.length > 0 ? reasonLabels[0] : undefined}
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
